import { supabase } from '../lib/supabase';
import { createClient } from '@supabase/supabase-js';

// Create a separate anon-only client for Edge Function calls
// This ensures Edge Functions are always called without user authentication
// and rely on SERVICE_ROLE internally
const anonClient = createClient(
  import.meta.env?.VITE_SUPABASE_URL,
  import.meta.env?.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false
    }
  }
);

// Convert database snake_case to React camelCase
const toReferralObject = (row) => ({
  id: row?.id,
  practitionerId: row?.practitioner_id,
  patientFirstName: row?.patient_first_name,
  patientLastName: row?.patient_last_name,
  patientYearOfBirth: row?.patient_year_of_birth,
  patientSex: row?.patient_sex,
  patientPhone: row?.patient_phone,
  patientEmail: row?.patient_email,
  referringDentistName: row?.referring_dentist_name,
  referringPracticeName: row?.referring_practice_name,
  referringDentistEmail: row?.referring_dentist_email,
  referringDentistPhone: row?.referring_dentist_phone,
  toothNumbers: row?.tooth_numbers || [],
  referralReasons: row?.referral_reasons || [],
  clinicalNotes: row?.clinical_notes,
  imagingDownloadLink: row?.imaging_download_link,
  imagingPassword: row?.imaging_password,
  imagingCenter: row?.imaging_center,
  status: row?.status,
  urgency: row?.urgency, // ONLY OpenAI urgency (routine/urgent/emergency)
  urgency_summary: row?.urgency_summary, // AI-generated summary
  submissionDate: row?.submission_date,
  reviewedAt: row?.reviewed_at,
  scheduledAt: row?.scheduled_at,
  completedAt: row?.completed_at,
  internalNotes: row?.internal_notes,
  internalNotesUpdatedAt: row?.internal_notes_updated_at,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
  publicToken: row?.public_token,
  pdfUrl: row?.pdf_url,
  pdfPath: row?.pdf_path,
  pdfBucket: row?.pdf_bucket,
  pdfCreatedAt: row?.pdf_created_at,
  pdfExpiresAt: row?.pdf_expires_at,
  pdfSizeBytes: row?.pdf_size_bytes
  // ✅ REMOVED: All OpenAI columns (extracted_tooth_numbers, urgency_classification, etc.)
  // ✅ System now uses ONLY OpenAI for urgency via Edge Function
});

export const referralService = {
  async getAll(practitionerId, filters = {}) {
    try {
      let query = supabase?.from('referrals')?.select('*');

      // Filter by practitioner only if practitionerId is provided
      // Super admin (practitionerId = null) sees all referrals
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      // CRITICAL FIX: Exclude draft referrals (only show finalized referrals)
      // Draft referrals are work-in-progress and should not appear in practitioner dashboard
      query = query?.neq('status', 'draft');

      query = query?.order('submission_date', { ascending: false });

      // Apply filters
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.urgency && filters?.urgency !== 'all') {
        query = query?.eq('urgency', filters?.urgency);
      }

      if (filters?.referringDentist) {
        query = query?.ilike('referring_dentist_name', `%${filters?.referringDentist}%`);
      }

      if (filters?.dateFrom) {
        query = query?.gte('submission_date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('submission_date', filters?.dateTo);
      }

      if (filters?.searchTerm) {
        query = query?.or(
          `patient_first_name.ilike.%${filters?.searchTerm}%,` +
          `patient_last_name.ilike.%${filters?.searchTerm}%,` +
          `referring_dentist_name.ilike.%${filters?.searchTerm}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(toReferralObject);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('referrals')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return toReferralObject(data);
    } catch (error) {
      console.error('Error fetching referral:', error);
      throw error;
    }
  },

  /**
   * Create referral via secure server-side Edge Function
   * This bypasses direct database access and uses SERVICE_ROLE authentication
   */
  async create(referralData) {
    try {
      console.log('\n🚀 ========== SECURE REFERRAL CREATION ==========');
      console.log('📍 Timestamp:', new Date()?.toISOString());
      console.log('📍 Method: Edge Function (create-referral)');
      console.log('📍 Authentication: SERVICE_ROLE (server-side)');
      
      // Validate required fields
      if (!referralData?.practitionerSlug) {
        throw new Error('practitioner_slug est requis pour créer un adressage');
      }
      
      if (!referralData?.patientInfo?.firstName || !referralData?.patientInfo?.lastName) {
        throw new Error('Informations patient incomplètes');
      }
      
      if (!referralData?.referringDentist?.fullName) {
        throw new Error('Informations dentiste adresseur incomplètes');
      }
      
      console.log('✅ All validations passed, calling Edge Function...');
      
      // Prepare payload for Edge Function
      const payload = {
        practitioner_slug: referralData?.practitionerSlug,
        patient_first_name: referralData?.patientInfo?.firstName,
        patient_last_name: referralData?.patientInfo?.lastName,
        patient_year_of_birth: referralData?.patientInfo?.yearOfBirth,
        patient_sex: referralData?.patientInfo?.sex,
        patient_phone: referralData?.patientInfo?.phone || null,
        patient_email: referralData?.patientInfo?.email || null,
        referring_dentist_name: referralData?.referringDentist?.fullName,
        referring_practice_name: referralData?.referringDentist?.practiceName || null,
        referring_dentist_email: referralData?.referringDentist?.email,
        referring_dentist_phone: referralData?.referringDentist?.phone,
        tooth_numbers: referralData?.toothNumbers || [],
        referral_reasons: referralData?.referralReasons || [],
        clinical_notes: referralData?.clinicalNotes,
        imaging_download_link: referralData?.imaging?.downloadLink || null,
        imaging_password: referralData?.imaging?.password || null,
        imaging_center: referralData?.imaging?.imagingCenter || null,
        honeypot: '' // Anti-spam field (should always be empty)
      };

      console.log('💾 Calling Edge Function with payload:');
      console.log('   - practitioner_slug:', payload?.practitioner_slug);
      console.log('   - patient:', payload?.patient_first_name, payload?.patient_last_name);
      console.log('   - referring_dentist:', payload?.referring_dentist_name);

      // Call Edge Function using anon client (no user auth context)
      const { data, error } = await anonClient?.functions?.invoke('create-referral', {
        body: payload
      });

      if (error) {
        console.error('\n❌ ========== EDGE FUNCTION ERROR ==========');
        console.error('📍 Function: create-referral');
        console.error('📍 Error:', error);
        console.error('📍 Message:', error?.message);
        console.error('==========================================\n');
        throw new Error(`Erreur lors de la création: ${error?.message}`);
      }

      if (!data?.referral_id || !data?.public_token) {
        console.error('❌ Invalid response from Edge Function:', data);
        throw new Error('Réponse invalide du serveur');
      }

      console.log('✅ Referral created successfully:');
      console.log('   - referral_id:', data?.referral_id);
      console.log('   - public_token:', data?.public_token?.substring(0, 8) + '...');
      console.log('========== SECURE REFERRAL CREATION END ==========\n');
      
      return {
        id: data?.referral_id,
        publicToken: data?.public_token
      };
    } catch (error) {
      console.error('❌ Error in referralService.create():', error);
      throw error;
    }
  },

  /**
   * Finalize referral via secure server-side Edge Function
   * Updates status from 'draft' to 'new' (submitted)
   */
  async finalize(referralId, publicToken, pdfBlob = null) {
    try {
      console.log('\n🚀 ========== SECURE REFERRAL FINALIZATION ==========');
      console.log('📍 Timestamp:', new Date()?.toISOString());
      console.log('📍 Method: Edge Function (finalize-referral)');
      console.log('📍 Authentication: SERVICE_ROLE (server-side)');
      console.log('📍 Referral ID:', referralId);
      console.log('📍 Public Token:', publicToken?.substring(0, 8) + '...');
      console.log('📍 PDF Blob provided:', !!pdfBlob);
      if (pdfBlob) {
        console.log('📍 PDF Blob size:', pdfBlob?.length, 'bytes (base64)');
      }
      
      // Validate required fields
      if (!referralId || !publicToken) {
        throw new Error('referral_id et public_token sont requis');
      }

      // Prepare payload for Edge Function
      const payload = {
        referral_id: referralId,
        public_token: publicToken
      };

      // Add PDF blob if provided (already in base64 format)
      if (pdfBlob) {
        payload.pdf_blob = pdfBlob;
        console.log('✅ pdf_blob added to payload, length:', pdfBlob?.length);
      } else {
        console.log('⚠️  No PDF blob provided, finalization will proceed without PDF');
      }

      console.log('💾 Calling Edge Function...');
      console.log('📍 finalize-referral called with pdf_blob:', !!pdfBlob);

      // Call Edge Function using anon client (no user auth context)
      const { data, error } = await anonClient?.functions?.invoke('finalize-referral', {
        body: payload
      });

      if (error) {
        console.error('\n❌ ========== EDGE FUNCTION ERROR ==========');
        console.error('📍 Function: finalize-referral');
        console.error('📍 Error:', error);
        console.error('📍 Message:', error?.message);
        console.error('==========================================\n');
        throw new Error(`Erreur lors de la finalisation: ${error?.message}`);
      }

      if (!data?.success) {
        console.error('❌ Finalization failed:', data);
        throw new Error('Échec de la finalisation');
      }

      console.log('✅ Referral finalized successfully:');
      console.log('   - referral_id:', data?.referral_id);
      console.log('   - status:', data?.status);
      console.log('========== SECURE REFERRAL FINALIZATION END ==========\n');
      
      return {
        success: true,
        referralId: data?.referral_id,
        status: data?.status
      };
    } catch (error) {
      console.error('❌ Error in referralService.finalize():', error);
      throw error;
    }
  },

  async updateStatus(id, status, additionalData = {}) {
    try {
      const updateData = {
        status,
        ...additionalData
      };

      // Set timestamp based on status
      if (status === 'reviewed' && !additionalData?.reviewed_at) {
        updateData.reviewed_at = new Date()?.toISOString();
      } else if (status === 'scheduled' && !additionalData?.scheduled_at) {
        updateData.scheduled_at = new Date()?.toISOString();
      } else if (status === 'completed' && !additionalData?.completed_at) {
        updateData.completed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('referrals')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return toReferralObject(data);
    } catch (error) {
      console.error('Error updating referral status:', error);
      throw error;
    }
  },

  async updateNotes(id, internalNotes) {
    try {
      console.log('Updating notes for referral:', id, 'with notes length:', internalNotes?.length);
      
      const { data, error } = await supabase
        ?.from('referrals')
        ?.update({ internal_notes: internalNotes })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Supabase error updating notes:', error);
        throw error;
      }
      
      if (!data) {
        throw new Error('No data returned from update operation');
      }
      
      console.log('Notes updated successfully:', data?.id);
      return toReferralObject(data);
    } catch (error) {
      console.error('Error updating referral notes:', error);
      throw error;
    }
  },

  async updateUrgency(id, urgency, urgencySummary = '') {
    try {
      const { data, error } = await supabase
        ?.from('referrals')
        ?.update({ 
          urgency,
          urgency_summary: urgencySummary,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return toReferralObject(data);
    } catch (error) {
      console.error('Error updating urgency:', error);
      throw error;
    }
  },

  async updateSummary(id, summary) {
    try {
      const { data, error } = await supabase
        ?.from('referrals')
        ?.update({ 
          urgency_summary: summary,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return toReferralObject(data);
    } catch (error) {
      console.error('Error updating summary:', error);
      throw error;
    }
  },

  async getNotesRevisions(referralId) {
    try {
      const { data, error } = await supabase
        ?.from('notes_revisions')
        ?.select('*')
        ?.eq('referral_id', referralId)
        ?.order('revision_number', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        referralId: row?.referral_id,
        revisionNumber: row?.revision_number,
        content: row?.content,
        changedBy: row?.changed_by,
        changedByEmail: row?.changed_by_email,
        changedByName: row?.changed_by_name,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching notes revisions:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('referrals')?.delete()?.eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting referral:', error);
      throw error;
    }
  },

  async getStatistics(practitionerId) {
    try {
      let query = supabase?.from('referrals')?.select('status, urgency, submission_date');

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      // Exclude draft referrals to match table display logic
      query = query?.neq('status', 'draft');

      const { data, error } = await query;

      if (error) throw error;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const stats = {
        totalPending: data?.filter(r => r?.status === 'new' || r?.status === 'reviewed')?.length,
        dailySubmissions: data?.filter(r => new Date(r.submission_date) >= today)?.length,
        urgentCount: data?.filter(r => 
          (r?.urgency === 'urgent' || r?.urgency === 'emergency') &&
          (r?.status !== 'completed' && r?.status !== 'cancelled')
        )?.length,
        byStatus: {
          new: data?.filter(r => r?.status === 'new')?.length,
          reviewed: data?.filter(r => r?.status === 'reviewed')?.length,
          scheduled: data?.filter(r => r?.status === 'scheduled')?.length,
          completed: data?.filter(r => r?.status === 'completed')?.length,
          cancelled: data?.filter(r => r?.status === 'cancelled')?.length
        }
      };

      return stats;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for PDF download from private storage
   * @param {string} pdfPath - Path to PDF in storage (e.g., 'referrals/{id}/adressage_Doe_John.pdf')
   * @param {number} expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
   * @returns {Promise<string>} Signed URL for PDF download
   */
  async getPdfSignedUrl(pdfPath, expiresIn = 3600) {
    try {
      if (!pdfPath) {
        throw new Error('Chemin du PDF manquant');
      }

      console.log('📥 Generating signed URL for PDF:', pdfPath);

      // CRITICAL FIX: Call Edge Function with SERVICE_ROLE access
      // The referral-pdfs bucket is private and requires SERVICE_ROLE key
      // Client-side anon key cannot access private storage
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (!session) {
        throw new Error('Session non trouvée. Veuillez vous reconnecter.');
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/get-pdf-signed-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pdfPath, expiresIn }),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Erreur lors de la génération de l\'URL');
      }

      const { signedUrl } = await response?.json();

      if (!signedUrl) {
        throw new Error('URL signée non générée');
      }

      console.log('✅ Signed URL generated successfully');
      return signedUrl;
    } catch (error) {
      console.error('Error generating PDF signed URL:', error);
      throw error;
    }
  }
};